import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Add,
  CreditCard,
  MoreVert,
  Block,
  CheckCircle,
  Star,
  LocalAtm,
  AccountBalance,
  Warning,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useBanking } from '../context/BankingContext';
import { formatCurrency, calculateCreditUtilization } from '../utils/mockData';

const CreditCards: React.FC = () => {
  const { user } = useAuth();
  const { creditCards, applyCreditCard, blockCreditCard, unblockCreditCard } = useBanking();
  
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedCardType, setSelectedCardType] = useState<'platinum' | 'gold' | 'standard'>('standard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCardId, setMenuCardId] = useState<string>('');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, cardId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuCardId(cardId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCardId('');
  };

  const handleApplyCreditCard = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      await applyCreditCard(selectedCardType);
      setSuccess(`${selectedCardType.charAt(0).toUpperCase() + selectedCardType.slice(1)} credit card application submitted successfully!`);
      setApplyDialogOpen(false);
    } catch (err) {
      setError('Failed to apply for credit card');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockCard = async (cardId: string) => {
    setLoading(true);
    try {
      await blockCreditCard(cardId);
      setSuccess('Credit card blocked successfully');
      handleMenuClose();
    } catch (err) {
      setError('Failed to block credit card');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockCard = async (cardId: string) => {
    setLoading(true);
    try {
      await unblockCreditCard(cardId);
      setSuccess('Credit card unblocked successfully');
      handleMenuClose();
    } catch (err) {
      setError('Failed to unblock credit card');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const userCreditCards = creditCards;
  const totalCreditUsed = userCreditCards.reduce((sum, card) => sum + card.currentBalance, 0);
  const totalCreditLimit = userCreditCards.reduce((sum, card) => sum + card.creditLimit, 0);
  const activeCreditCards = userCreditCards.filter(card => card.status === 'active').length;
  const blockedCreditCards = userCreditCards.filter(card => card.status === 'blocked').length;

  const getCardIcon = (cardType: string) => {
    switch (cardType) {
      case 'platinum':
        return <Star sx={{ color: '#E5E4E2' }} />;
      case 'gold':
        return <Star sx={{ color: '#FFD700' }} />;
      case 'standard':
        return <CreditCard color="primary" />;
      default:
        return <CreditCard color="primary" />;
    }
  };

  const getCardColor = (cardType: string) => {
    switch (cardType) {
      case 'platinum':
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'gold':
        return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'standard':
        return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
      default:
        return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'success' : 'error';
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization >= 80) return 'error';
    if (utilization >= 60) return 'warning';
    return 'success';
  };

  const cardTypeInfo = {
    standard: {
      title: 'Standard Card',
      creditLimit: '$5,000',
      benefits: ['1% cashback on all purchases', 'No annual fee', 'Online banking'],
      icon: <CreditCard color="primary" />
    },
    gold: {
      title: 'Gold Card',
      creditLimit: '$10,000',
      benefits: ['2% cashback on all purchases', '1.5x points on dining', 'Priority customer service'],
      icon: <Star sx={{ color: '#FFD700' }} />
    },
    platinum: {
      title: 'Platinum Card',
      creditLimit: '$15,000',
      benefits: ['3% cashback on all purchases', '2x points on travel', 'Concierge service', 'Airport lounge access'],
      icon: <Star sx={{ color: '#E5E4E2' }} />
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Credit Cards
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setApplyDialogOpen(true)}
          disabled={loading}
        >
          Apply for Card
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Credit Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocalAtm color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Total Credit Limit</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {formatCurrency(totalCreditLimit)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Across {userCreditCards.length} card{userCreditCards.length !== 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalance color="info" sx={{ mr: 1 }} />
              <Typography variant="h6">Available Credit</Typography>
            </Box>
            <Typography variant="h4" color="info.main">
              {formatCurrency(totalCreditLimit - totalCreditUsed)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalCreditLimit > 0 ? `${(100 - calculateCreditUtilization(totalCreditUsed, totalCreditLimit)).toFixed(1)}%` : '0%'} available
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CheckCircle color="success" sx={{ mr: 1 }} />
              <Typography variant="h6">Active Cards</Typography>
            </Box>
            <Typography variant="h4" color="success.main">
              {activeCreditCards}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {blockedCreditCards > 0 && `${blockedCreditCards} blocked`}
            </Typography>
          </CardContent>
        </Card>

        {totalCreditLimit > 0 && (
          <Card sx={{ flex: '1 1 250px', minWidth: '250px' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Warning color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Credit Utilization</Typography>
              </Box>
              <Typography variant="h4" color={getUtilizationColor(calculateCreditUtilization(totalCreditUsed, totalCreditLimit))}>
                {calculateCreditUtilization(totalCreditUsed, totalCreditLimit).toFixed(1)}%
              </Typography>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={calculateCreditUtilization(totalCreditUsed, totalCreditLimit)}
                  color={getUtilizationColor(calculateCreditUtilization(totalCreditUsed, totalCreditLimit))}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatCurrency(totalCreditUsed)} used
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Credit Cards List */}
      <Typography variant="h5" sx={{ mb: 3 }}>
        Your Credit Cards
      </Typography>

      {userCreditCards.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CreditCard sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Credit Cards Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Apply for your first credit card to start building your credit history.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setApplyDialogOpen(true)}
            >
              Apply for Credit Card
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {userCreditCards.map((card) => {
            const utilization = calculateCreditUtilization(card.currentBalance, card.creditLimit);
            return (
              <Card key={card.id} sx={{ position: 'relative', overflow: 'visible' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* Card Visual */}
                      <Box
                        sx={{
                          width: 80,
                          height: 50,
                          borderRadius: 1,
                          background: getCardColor(card.cardType),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.8rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {getCardIcon(card.cardType)}
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6">
                            {card.cardType.charAt(0).toUpperCase() + card.cardType.slice(1)} Card
                          </Typography>
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: card.status === 'active' ? 'success.main' : 'error.main',
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          >
                            {card.status}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {card.cardNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Expires: {card.expiryDate}
                        </Typography>
                      </Box>
                    </Box>

                    {user?.role === 'admin' && (
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, card.id)}
                        disabled={loading}
                      >
                        <MoreVert />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Current Balance
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {formatCurrency(card.currentBalance)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Credit Limit
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(card.creditLimit)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Available Credit
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(card.creditLimit - card.currentBalance)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Credit Utilization
                      </Typography>
                      <Typography variant="body2" color={getUtilizationColor(utilization)}>
                        {utilization.toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={utilization}
                      color={getUtilizationColor(utilization)}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Apply for Credit Card Dialog */}
      <Dialog 
        open={applyDialogOpen} 
        onClose={() => setApplyDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Apply for Credit Card</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose the credit card that best fits your needs. All applications are subject to credit approval.
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 2 }}>Select Card Type</FormLabel>
            <RadioGroup
              value={selectedCardType}
              onChange={(e) => setSelectedCardType(e.target.value as 'platinum' | 'gold' | 'standard')}
            >
              {Object.entries(cardTypeInfo).map(([type, info]) => (
                <Box key={type} sx={{ mb: 2 }}>
                  <FormControlLabel
                    value={type}
                    control={<Radio />}
                    label={
                      <Card variant="outlined" sx={{ width: '100%', ml: 1 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            {info.icon}
                            <Box>
                              <Typography variant="h6">{info.title}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Credit Limit: {info.creditLimit}
                              </Typography>
                            </Box>
                          </Box>
                          <List dense sx={{ p: 0 }}>
                            {info.benefits.map((benefit, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 20 }}>
                                  <CheckCircle color="success" sx={{ fontSize: 16 }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={benefit}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApplyCreditCard}
            disabled={loading}
          >
            Apply Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Menu for admin actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {menuCardId && (
          <>
            {creditCards.find(card => card.id === menuCardId)?.status === 'active' ? (
              <MenuItem onClick={() => handleBlockCard(menuCardId)}>
                <Block sx={{ mr: 1 }} />
                Block Card
              </MenuItem>
            ) : (
              <MenuItem onClick={() => handleUnblockCard(menuCardId)}>
                <CheckCircle sx={{ mr: 1 }} />
                Unblock Card
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </Container>
  );
};

export default CreditCards; 